import { useState } from "react";
import { Button, Card, StatusIndicator, type StatusTone } from "./ui";
import { EmptyState } from "./states";
import { SupportLink } from "./support-link";
import { formatDate } from "./billing";
import type { RefundPreviewView, RefundRequestState, RefundView } from "../lib/api";

/**
 * Refund UI (Part 8).
 *
 * WHAT THIS SCREEN IS NOT ALLOWED TO DO
 *  - It never asks for a card number, IBAN, CVV or account detail. There is no
 *    input here that could hold one, and the server rejects payment-shaped text in
 *    the explanation field anyway. Money goes back the way it came; there is no
 *    destination to choose, so there is no destination to type.
 *  - It never says a refund is guaranteed. Submitting creates a REQUEST. The
 *    button says so, the confirmation says so, and the resulting card says
 *    "Requested" rather than a figure that looks like a credit.
 *  - It never denies a statutory right through wording. When ODIN cannot send a
 *    refund automatically, the copy says a person will arrange it — it does not say
 *    the customer cannot have one.
 *  - It never uses a retention gauntlet. Withdrawing a request is one button.
 *  - It never suggests the bot will be removed. The consequence list comes from the
 *    server and states the opposite explicitly.
 */

export function refundTone(state: RefundRequestState): StatusTone {
  switch (state) {
    case "COMPLETED":
      return "ok";
    case "APPROVED":
    case "PARTIALLY_APPROVED":
    case "PROCESSING":
      return "warn";
    case "DENIED":
    case "FAILED":
      return "danger";
    default:
      return "neutral";
  }
}

/**
 * What a state means for the person waiting. Deliberately plain: "Approved" on its
 * own reads like the money has arrived, and it has not.
 */
export function refundStateHelp(refund: RefundView): string {
  switch (refund.state) {
    case "SUBMITTED":
      return "We have your request. Nothing has been charged or refunded yet.";
    case "UNDER_REVIEW":
      return "Someone at ODIN is looking at this now.";
    case "INFORMATION_REQUESTED":
      return "We need a little more from you before we can decide. Check the note below.";
    case "APPROVED":
      return "Approved. The money has not moved yet — we send it to your payment provider next.";
    case "PARTIALLY_APPROVED":
      return "Approved in part. The money has not moved yet — we send it next.";
    case "PROCESSING":
      return "Sent to your payment provider. Banks usually take five to ten working days to show it.";
    case "COMPLETED":
      return "Refunded to the payment method you originally used. Your bank may take a few days to display it.";
    case "DENIED":
      return "We could not approve this one. The reason is below.";
    case "FAILED":
      return "The payment provider could not complete this. No money moved. Message refund support below and we will sort it by hand.";
    case "CANCELLED":
      return "You withdrew this request. Nothing happened to your payment or your servers.";
    default:
      return "";
  }
}

export function RefundCard({
  refund,
  onWithdraw,
  withdrawing = false,
  error = null,
}: {
  refund: RefundView;
  onWithdraw?: (refund: RefundView) => void;
  withdrawing?: boolean;
  error?: string | null;
}): JSX.Element {
  return (
    <Card title={`Refund request · ${refund.reasonLabel}`}>
      <div style={{ display: "grid", gap: "0.6rem" }}>
        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <StatusIndicator tone={refundTone(refund.state)} label={refund.stateLabel} />
        </div>
        <p className="muted">{refundStateHelp(refund)}</p>

        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Requested</span>
            <span className="kv__v">
              {refund.requested.formatted} {refund.currency}
            </span>
          </div>
          {refund.approved ? (
            <div className="kv">
              <span className="kv__k">Approved</span>
              <span className="kv__v">{refund.approved.formatted}</span>
            </div>
          ) : null}
          {/* Shown only once it is real. A zero here before completion would read as
              a refusal, when in fact nothing has been sent yet. */}
          {refund.refunded.minor > 0 ? (
            <div className="kv">
              <span className="kv__k">Refunded</span>
              <span className="kv__v">{refund.refunded.formatted}</span>
            </div>
          ) : null}
          <div className="kv">
            <span className="kv__k">Asked on</span>
            <span className="kv__v">{formatDate(refund.createdAtIso)}</span>
          </div>
          {refund.decidedAtIso ? (
            <div className="kv">
              <span className="kv__k">Decided</span>
              <span className="kv__v">{formatDate(refund.decidedAtIso)}</span>
            </div>
          ) : null}
          {refund.completedAtIso ? (
            <div className="kv">
              <span className="kv__k">Completed</span>
              <span className="kv__v">{formatDate(refund.completedAtIso)}</span>
            </div>
          ) : null}
        </div>

        <div>
          <p className="subhead">What you told us</p>
          <p className="muted">{refund.explanation}</p>
        </div>

        {refund.decisionNote ? (
          <div>
            <p className="subhead">Our reply</p>
            <p className="muted">{refund.decisionNote}</p>
          </div>
        ) : null}

        {/* Part 10.2 — refund follow-up. Shown on every request, not only the ones
            that went wrong, because "we said no" and "you may not ask again" are
            different things and Part 8.5 forbids wording that conflates them. The
            link goes to the support desk, which cannot itself move money: a refund
            stays an explicit, separately authorised action. */}
        <p className="muted">
          Disagree with this, or something looks wrong?{" "}
          <SupportLink topic="REFUND">Refund support</SupportLink> — asking there does not affect
          your statutory rights, and it never moves money on its own.
        </p>

        {error ? (
          <p className="ax-confirm__error" role="alert">
            {error}
          </p>
        ) : null}

        {/* One button, no "are you sure you want to give up your refund" screen.
            Part 8.5 forbids deceptive cancellation patterns in both directions. */}
        {refund.canCancel && onWithdraw ? (
          <div className="row">
            <Button variant="secondary" onClick={() => onWithdraw(refund)} disabled={withdrawing}>
              {withdrawing ? "Withdrawing…" : "Withdraw this request"}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * How the request will be HANDLED — never whether money is coming back.
 *
 * The server's own word for the automatic outcome is "ineligible" when a payment
 * falls outside the automatic window. Rendering that word would tell the customer
 * they have no claim, which is both untrue and, under Norwegian and EU consumer
 * law, not ODIN's call to make. So the wording here describes who decides, and the
 * statutory position is left exactly where the law puts it.
 */
export function refundOutcomeHelp(preview: RefundPreviewView): string {
  if (preview.outcome === "eligible") {
    return "This looks straightforward. We still check every request before any money moves.";
  }
  if (preview.canRequestManualReview) {
    return "A member of our billing team will look at this one personally. You can still send it.";
  }
  return "";
}

export function RefundRequestForm({
  preview,
  onSubmit,
  onDismiss,
  pending = false,
  error = null,
}: {
  preview: RefundPreviewView;
  onSubmit: (input: { reason: string; explanation: string }) => void;
  onDismiss: () => void;
  pending?: boolean;
  error?: string | null;
}): JSX.Element {
  const [reason, setReason] = useState<string>(preview.reasonOptions[0]?.code ?? "OTHER");
  const [explanation, setExplanation] = useState("");
  const tooShort = explanation.trim().length < 10;
  /**
   * Two situations where sending another request would achieve nothing: one is
   * already open on this payment, or there is no refundable balance left at all.
   * Neither is a refusal, so neither is worded as one — and both still tell the
   * customer how to reach a person, because a system that says "no" and closes
   * the door is exactly the deceptive pattern Part 8.5 rules out.
   */
  const alreadyOpen = preview.existingOpenRequestId !== null;
  const nothingLeft = preview.maxRefundable.minor <= 0;
  const canSend = !alreadyOpen && !nothingLeft;
  const outcomeHelp = refundOutcomeHelp(preview);

  return (
    <section className="ax-confirm" aria-label="Request a refund">
      <h4 className="ax-confirm__title">Ask for a refund</h4>
      <p className="ax-confirm__subtitle">
        You paid {preview.amountPaid.formatted} {preview.currency}.{" "}
        {preview.alreadyRefunded.minor > 0
          ? `${preview.alreadyRefunded.formatted} has already been refunded, so up to ${preview.maxRefundable.formatted} is left.`
          : `Up to ${preview.maxRefundable.formatted} can be refunded.`}
      </p>

      {outcomeHelp ? <p className="muted">{outcomeHelp}</p> : null}

      {alreadyOpen ? (
        <p className="ax-metric__foot">
          You already have a request open on this payment. We are working through it —
          there is no need to send another one, and sending one would not make it faster.
        </p>
      ) : null}

      {!alreadyOpen && nothingLeft ? (
        <p className="ax-metric__foot">
          There is nothing left to refund on this payment. If you think that is wrong,{" "}
          <SupportLink topic="REFUND">refund support</SupportLink> will check it with you — a
          person, in a conversation kept on your account.
        </p>
      ) : null}

      {/* The honest part. This is stated BEFORE they fill anything in, not after
          they submit and discover a person has to do it. */}
      {canSend && !preview.canBeSentAutomatically ? (
        <p className="ax-metric__foot">
          ODIN cannot send this one automatically, so our billing team will arrange it
          by hand. You can still ask here — this does not affect your right to a refund.
        </p>
      ) : null}

      {preview.reasons.length > 0 ? (
        <ul className="ax-confirm__list">
          {preview.reasons.map((line) => (
            <li key={line} className="muted">
              {line}
            </li>
          ))}
        </ul>
      ) : null}

      <div>
        <p className="subhead">What happens if this goes through</p>
        <ul className="ax-confirm__list">
          {preview.consequences.map((line) => (
            <li key={line} className="muted">
              {line}
            </li>
          ))}
        </ul>
      </div>

      {!canSend ? (
        <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
          <button type="button" className="ax-btn ax-btn--secondary" onClick={onDismiss}>
            Close
          </button>
        </div>
      ) : (
      <>
      <div className="field">
        <label className="field__label" htmlFor="refund-reason">
          Reason
        </label>
        <select
          id="refund-reason"
          className="field__input"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={pending}
        >
          {preview.reasonOptions.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="refund-explanation">
          What happened?
        </label>
        <textarea
          id="refund-explanation"
          className="field__input"
          rows={4}
          value={explanation}
          onChange={(event) => setExplanation(event.target.value)}
          disabled={pending}
          placeholder="A sentence or two is plenty."
        />
        {/* Said plainly, so nobody types a card number and has it rejected. */}
        <p className="ax-metric__foot">
          Please do not include card, bank or payment details — we never need them, and
          anything that looks like one will be rejected without being stored.
        </p>
      </div>

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
        {/* "Send my request", never "Refund me". Pressing this does not move money. */}
        <button
          type="button"
          className="ax-btn ax-btn--danger"
          onClick={() => onSubmit({ reason, explanation: explanation.trim() })}
          disabled={pending || tooShort}
        >
          {pending ? "Sending…" : "Send my request"}
        </button>
        <button type="button" className="ax-btn ax-btn--secondary" onClick={onDismiss} disabled={pending}>
          Not now
        </button>
      </div>
      <p className="ax-metric__foot">
        This sends a request to our billing team. It is not an automatic refund, and
        submitting it changes nothing about your plan or your Discord servers.
      </p>
      </>
      )}
    </section>
  );
}

/**
 * Refund history. Read-only for the customer: there is no control on this list that
 * approves, executes or re-prices anything — the only action available is
 * withdrawing your own open request.
 */
export function RefundList({
  refunds,
  onWithdraw,
  withdrawingId = null,
  errorId = null,
  error = null,
}: {
  refunds: RefundView[];
  onWithdraw?: (refund: RefundView) => void;
  withdrawingId?: string | null;
  errorId?: string | null;
  error?: string | null;
}): JSX.Element {
  if (refunds.length === 0) {
    return (
      <EmptyState title="No refund requests">
        If something was charged in error, you can ask for it back from the invoice it
        was charged on. Asking creates a request — it never takes ODIN out of your
        Discord servers.
      </EmptyState>
    );
  }
  return (
    <div className="card-grid">
      {refunds.map((refund) => (
        <RefundCard
          key={refund.id}
          refund={refund}
          onWithdraw={onWithdraw}
          withdrawing={withdrawingId === refund.id}
          error={errorId === refund.id ? error : null}
        />
      ))}
    </div>
  );
}
